import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, ViewEncapsulation, } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { RouterModule } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TablerIconsModule } from 'angular-tabler-icons';
import { NgScrollbarModule } from 'ngx-scrollbar';
import { AppSettings } from 'src/app/config';
import { TokenService } from 'src/app/core/services/token.service';
import { MaterialModule } from 'src/app/material.module';
import { SysService } from 'src/app/modules/sys/services/sys.service';
import { CoreService } from 'src/app/shared/services/core.service';
import { environment } from '../../../../../environments/environment';
import { AuthService } from '../../../../modules/auth/services/auth.service';
import { navItems } from '../sidebar/sidebar-data';

interface notifications {
  id: number;
  img: string;
  title: string;
  subtitle: string;
}

interface profiledd {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
  color: string;
}

interface apps {
  id: number;
  img: string;
  title: string;
  subtitle: string;
  link: string;
}

interface quicklinks {
  id: number;
  title: string;
  link: string;
}

@Component({
  selector: 'app-header',
  imports: [
    RouterModule,
    CommonModule,
    NgScrollbarModule,
    TablerIconsModule,
    MaterialModule,
    TranslateModule
  ],
  templateUrl: './header.component.html',
  encapsulation: ViewEncapsulation.None,
})
export class HeaderComponent implements OnInit {
  @Input() showToggle = true;
  @Input() toggleChecked = false;
  @Output() toggleMobileNav = new EventEmitter<void>();
  @Output() toggleMobileFilterNav = new EventEmitter<void>();
  @Output() toggleCollapsed = new EventEmitter<void>();

  isCollapse: boolean = false; // Initially hidden
  savedLang: any;
  public selectedLanguageCode: 'en' | 'ar' = 'en';

  toggleCollpase() {
    this.isCollapse = !this.isCollapse; // Toggle visibility
  }

  showFiller = false;

  public selectedLanguage: any;

  public languages: any[] = [
    {
      language: 'English',
      code: 'en',
      type: 'US',
      icon: '/assets/images/flag/icon-flag-en.svg',
    },
    {
      language: 'العربية',
      code: 'ar',
      icon: '/assets/images/flag/saudi-arabia-flag-icon.svg',
    },
  ];

  @Output() optionsChange = new EventEmitter<AppSettings>();
  loading: boolean = false;
  userId: number;
  userTypeId: number;
  userInfo: any = null; // Property to store user details
  imageBaseUrl = environment.imageBaseUrl;
  photo = 'EmployeePhoto.jpg';
  userType: string | undefined;
  userName: any = null;

  currentPreferenceId: number | null = null;
  preferenceData: any;

  constructor(
    private settings: CoreService,
    public dialog: MatDialog,
    private translate: TranslateService,
    private authService: AuthService,
    private tokenService: TokenService,
    private sysService: SysService
  ) {
    this.userId = this.tokenService.getUserIdFromToken();
    this.userTypeId = this.tokenService.getUserTypeIdFromToken();
  }

  ngOnInit(): void {
    this.getUserLanguage();
    this.getUserInfo(this.userTypeId);
  }

  /* ================= Language Logic ================= */

  getUserLanguage(): void {
    this.sysService.getUserPreference(this.userId, 'PREFERENCE_LANGUAGE').subscribe({
      next: (res: any) => {
        if (res?.data) {
          this.currentPreferenceId = res.data.id;
          this.preferenceData = res.data;
          if (this.preferenceData) {
            this.applyLanguage(res.data.preferenceValue);
          }
          else {
            this.applyLanguage("en");
          }
        } else {
          this.applyLanguage('en');
        }
      },
      error: () => this.applyLanguage('en'),
    });
  }

  applyLanguage(langCode: 'en' | 'ar'): void {
    this.selectedLanguageCode = langCode;
    this.selectedLanguage = this.languages.find(l => l.code === langCode)!;

    localStorage.setItem('lang', JSON.stringify(this.selectedLanguage));
    this.translate.use(langCode);

    document.documentElement.lang = langCode;
    document.documentElement.dir = langCode === 'ar' ? 'rtl' : 'ltr';
    this.settings.setOptions({ dir: document.documentElement.dir as 'rtl' | 'ltr' });
  }

  saveLanguage(): void {
    const preference: any = {
      userId: this.userId,
      PreferenceCode: 'PREFERENCE_LANGUAGE',
      PreferenceValue: this.selectedLanguageCode,
      isActive: true,
      creationDate: this.preferenceData?.creationDate ?? new Date(),
      creationUserId: this.preferenceData?.creationUserId ?? this.userId,
      ...(this.currentPreferenceId && { id: this.currentPreferenceId }),
    };

    this.loading = true;

    this.sysService.SaveUserPreference(preference).subscribe({
      next: (res: any) => {
        if (res?.isSuccess) {
          this.currentPreferenceId = res.data?.id;
          this.applyLanguage(this.selectedLanguageCode);
          setTimeout(() => window.location.reload(), 100);
        }
      },
      error: () => (this.loading = false),
      complete: () => (this.loading = false),
    });
  }

  changeLanguage(langCode: 'en' | 'ar'): void {
    this.applyLanguage(langCode);
    this.saveLanguage();
  }

  // Logout functionality
  logout() {
    this.loading = true; // Start loading

    try {
      this.authService.logout();
    } catch (err) {
      console.error('Logout failed', err);
    } finally {
      this.loading = false; // Stop loading
    }
  }

  // Define the mapping of userTypeId to userType string
  private userTypeMap: { [key: number]: string } = {
    1: 'Customer',
    2: 'Employee',
    3: 'Merchant',
    4: 'Admin',
    5: 'Promoter',
    6: 'Community'
  };

  getUserInfo(userTypeId: number) {
    let controllerRoute = '';

    if (!this.userId) {
      console.error('User ID is not available');
      return;
    }

    let userType = this.userTypeMap[userTypeId];

    if (!userType) {
      console.error('Invalid user type ID:', userTypeId);
      return;
    }

    controllerRoute = (userType === 'Admin') ? 'Employee' : userType;

    this.userType = controllerRoute;

    this.authService.getInfoByUserId(controllerRoute, this.userId).subscribe(
      (response) => {

        if (!response?.data) {
          console.warn('No linked user data found:', response?.message);
          return;
        }

        this.userInfo = response.data;
        const currentLang = this.translate.currentLang || this.selectedLanguage?.code || 'en';

        if (currentLang === 'ar') {
          if (this.userInfo.firstNameAr && this.userInfo.lastNameAr) {
            this.userName = this.userInfo.firstNameAr + " " + this.userInfo.lastNameAr;
          } else if (this.userInfo.nameAr) {
            this.userName = this.userInfo.nameAr;
          } else {
            this.userName = (this.userInfo.firstNameEn && this.userInfo.lastNameEn)
              ? this.userInfo.firstNameEn + " " + this.userInfo.lastNameEn
              : this.userInfo.nameEn;
          }
        } else {
          this.userName = (this.userInfo.firstNameEn && this.userInfo.lastNameEn)
            ? this.userInfo.firstNameEn + " " + this.userInfo.lastNameEn
            : this.userInfo.nameEn;
        }
      },
      (error) => {
        //   console.error('Failed to fetch user info', error);
      }
    );
  }


  get options() {
    return this.settings.getOptions();
  }

  openDialog() {
    const dialogRef = this.dialog.open(AppSearchDialogComponent);

    dialogRef.afterClosed().subscribe((result) => {
    });
  }

  setlightDark(theme: string) {
    this.settings.setOptions({ theme });
    this.emitOptions();
  }
  private emitOptions() {
    this.optionsChange.emit(this.options);
  }

  notifications: notifications[] = [
    {
      id: 1,
      img: '/assets/images/profile/user-1.jpg',
      title: 'Roman Joined the Team!',
      subtitle: 'Congratulate him sf',
    },
    {
      id: 2,
      img: '/assets/images/profile/user-2.jpg',
      title: 'New message received',
      subtitle: 'Salma sent you new message',
    },
    {
      id: 3,
      img: '/assets/images/profile/user-3.jpg',
      title: 'New Payment received',
      subtitle: 'Check your earnings',
    },
    {
      id: 4,
      img: '/assets/images/profile/user-4.jpg',
      title: 'Jolly completed tasks',
      subtitle: 'Assign her new tasks',
    },
    {
      id: 5,
      img: '/assets/images/profile/user-5.jpg',
      title: 'Hitesh Joined thed Team!',
      subtitle: 'Congratulate him',
    },
  ];

  profiledd: profiledd[] = [
    {
      id: 1,
      img: 'wallet',
      color: 'primary',
      title: 'My Profile',
      subtitle: 'Account Settings',
      link: '/',
    },
    {
      id: 2,
      img: 'shield',
      color: 'success',
      title: 'My Inbox',
      subtitle: 'Messages & Email',
      link: '/',
    },
    {
      id: 3,
      img: 'credit-card',
      color: 'error',
      title: 'My Tasks',
      subtitle: 'To-do and Daily Tasks',
      link: '/',
    },
  ];

  apps: apps[] = [
    {
      id: 1,
      img: '/assets/images/svgs/icon-dd-chat.svg',
      title: 'Chat Application',
      subtitle: 'Messages & Emails',
      link: '/apps/chat',
    },
    {
      id: 2,
      img: '/assets/images/svgs/icon-dd-cart.svg',
      title: 'Todo App',
      subtitle: 'Completed task',
      link: '/apps/todo',
    },
    {
      id: 3,
      img: '/assets/images/svgs/icon-dd-invoice.svg',
      title: 'Invoice App',
      subtitle: 'Get latest invoice',
      link: '/apps/invoice',
    },
    {
      id: 4,
      img: '/assets/images/svgs/icon-dd-date.svg',
      title: 'Calendar App',
      subtitle: 'Get Dates',
      link: '/apps/calendar',
    },
    {
      id: 5,
      img: '/assets/images/svgs/icon-dd-mobile.svg',
      title: 'Contact Application',
      subtitle: '2 Unsaved Contacts',
      link: '/apps/contacts',
    },
    {
      id: 6,
      img: '/assets/images/svgs/icon-dd-lifebuoy.svg',
      title: 'Tickets App',
      subtitle: 'Create new ticket',
      link: '/apps/tickets',
    },
    {
      id: 7,
      img: '/assets/images/svgs/icon-dd-message-box.svg',
      title: 'Email App',
      subtitle: 'Get new emails',
      link: '/apps/email/inbox',
    },
    {
      id: 8,
      img: '/assets/images/svgs/icon-dd-application.svg',
      title: 'Courses',
      subtitle: 'Create new course',
      link: '/apps/courses',
    },
  ];

  quicklinks: quicklinks[] = [
    {
      id: 1,
      title: 'Pricing Page',
      link: '/theme-pages/pricing',
    },
    {
      id: 2,
      title: 'Authentication Design',
      link: '/authentication/login',
    },
    {
      id: 3,
      title: 'Register Now',
      link: '/authentication/side-register',
    },
    {
      id: 4,
      title: '404 Error Page',
      link: '/authentication/error',
    },
    {
      id: 5,
      title: 'Notes App',
      link: '/apps/notes',
    },
    {
      id: 6,
      title: 'Employee App',
      link: '/apps/employee',
    },
    {
      id: 7,
      title: 'Todo Application',
      link: '/apps/todo',
    },
    {
      id: 8,
      title: 'Treeview',
      link: '/theme-pages/treeview',
    },
  ];
}

@Component({
  selector: 'search-dialog',
  imports: [RouterModule, MaterialModule, TablerIconsModule, FormsModule],
  templateUrl: 'search-dialog.component.html',
})
export class AppSearchDialogComponent {
  searchText: string = '';
  navItems = navItems;

  navItemsData = navItems.filter((navitem) => navitem.displayName);

  // filtered = this.navItemsData.find((obj) => {
  //   return obj.displayName == this.searchinput;
  // });
}
